/**
 * ChangePasswordRequiredPage — принудительная смена пароля при первом входе.
 *
 * Открывается, когда у родителя в БД флаг must_change_password=true
 * (например, после сброса пароля администратором).
 *
 * Логика доступа:
 * - user отсутствует → редирект на /login
 * - must_change_password=false → редирект на / (страница не нужна)
 * - иначе → форма смены, без боковой/нижней навигации
 */
import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box, Container, Paper, Stack, Text, Title, Center, Loader, Button, Group,
} from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { useParentSession } from '../context/ParentSessionContext';
import ChangePasswordCard from '../components/ChangePasswordCard';
import tokens from '../theme/designTokens';

const ChangePasswordRequiredPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useParentSession();

  if (loading) {
    return (
      <Center style={{ minHeight: '100vh', background: tokens.colors.bg.page }}>
        <Loader color="brand-purple" />
      </Center>
    );
  }

  // Если не залогинен — отправляем на логин
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Если флаг уже снят — страница не нужна, отправляем на главную
  if (!user.must_change_password) {
    return <Navigate to="/" replace />;
  }

  const handleSuccess = () => {
    navigate('/', { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box style={{ minHeight: '100vh', background: tokens.colors.bg.page, paddingTop: 60, paddingBottom: 60 }}>
      <Container size="xs">
        {/* Логотип */}
        <Center mb="xl">
          <Box style={{ textAlign: 'center' }}>
            <Box
              style={{
                width: 80,
                height: 80,
                borderRadius: 24,
                background: tokens.gradients.hero,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                fontSize: 32,
                boxShadow: '0 12px 32px rgba(124,58,237,0.3)',
                marginBottom: 16,
              }}
            >
              ЭИ
            </Box>
            <Title
              order={2}
              style={{
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                color: tokens.colors.text.primary,
              }}
            >
              Это <span style={{ color: tokens.colors.brand.orange }}>Интересно!</span>
            </Title>
            <Text c={tokens.colors.text.muted} fw={700} mt={4}>
              Личный кабинет родителя
            </Text>
          </Box>
        </Center>

        <Paper p="xl" radius="xl" shadow="md" style={{ background: 'white' }}>
          <Stack gap="md">
            <Box>
              <Title order={3} mb={4} style={{ color: tokens.colors.text.primary }}>
                Требуется смена пароля
              </Title>
              <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                Для безопасности аккаунта установите новый пароль перед продолжением работы.
              </Text>
              {user.email && (
                <Text size="xs" c={tokens.colors.text.muted} mt={8}>
                  Вы вошли как <b>{user.email}</b>
                </Text>
              )}
            </Box>

            <ChangePasswordCard onSuccess={handleSuccess} submitLabel="Сменить пароль и продолжить" />

            <Group justify="center">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconLogout size={16} />}
                onClick={handleLogout}
              >
                Выйти из аккаунта
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangePasswordRequiredPage;
